import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { atom, getDefaultStore } from 'jotai/vanilla';
import type { Location, To, NavigateOptions, UIMatch } from 'react-router-dom';
import { createBrowserRouter } from 'react-router-dom';

type ReactRouter = ReturnType<typeof createBrowserRouter>;

type RouterState = ReactRouter['state'];

type NavigateToArgs = {
  to: To;
  options?: NavigateOptions;
};

type SetSearchParamsArgs = {
  params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams);
  options?: NavigateOptions;
};

// Router instance storage
let routerInstance: ReactRouter | null = null;

// Primitive atom for router state - updated via store.set() when router state changes
const routerStateAtom = atom<RouterState | null>(null);

// Derived atom that asserts router state is initialized
// Safe because createJotaiRouter is called at module level before React renders
const routerStateReadyAtom = atom<RouterState>((get) => {
  const state = get(routerStateAtom);
  if (!state) {
    throw new Error(
      'Router not initialized. Ensure createJotaiRouter is called before rendering.'
    );
  }
  return state;
});

// Location atom (writable)
export const locationAtom = atom<
  Location,
  [NavigateToArgs | To | number],
  Promise<void>
>(
  (get) => {
    const state = get(routerStateReadyAtom);
    return state.location;
  },
  async (_get, _set, update) => {
    if (!routerInstance) {
      throw new Error('Router not initialized');
    }

    if (typeof update === 'number') {
      await routerInstance.navigate(update);
      return;
    }

    if (typeof update === 'object' && 'to' in update) {
      await routerInstance.navigate(update.to, update.options);
      return;
    }

    await routerInstance.navigate(update);
  }
);

// Matches atom (read-only)
export const matchesAtom = atom<UIMatch[]>((get) => {
  const state = get(routerStateReadyAtom);
  return state.matches.map((match) => ({
    id: match.route.id,
    pathname: match.pathname,
    params: match.params,
    data: state.loaderData[match.route.id],
    handle: match.route.handle,
  }));
});

// Search params atom (writable)
export const searchParamsAtom = atom<
  URLSearchParams,
  [
    | SetSearchParamsArgs
    | URLSearchParams
    | ((prev: URLSearchParams) => URLSearchParams)
  ],
  Promise<void>
>(
  (get) => {
    const state = get(routerStateReadyAtom);
    return new URLSearchParams(state.location.search);
  },
  async (get, _set, update) => {
    if (!routerInstance) {
      throw new Error('Router not initialized');
    }

    const currentState = get(routerStateReadyAtom);

    let newParams: URLSearchParams;
    let options: NavigateOptions | undefined;

    if (typeof update === 'object' && 'params' in update) {
      options = update.options;
      newParams =
        typeof update.params === 'function'
          ? update.params(new URLSearchParams(currentState.location.search))
          : update.params;
    } else if (typeof update === 'function') {
      newParams = update(new URLSearchParams(currentState.location.search));
    } else {
      newParams = update;
    }

    await routerInstance.navigate(
      {
        pathname: currentState.location.pathname,
        search: newParams.toString() ? `?${newParams.toString()}` : '',
        hash: currentState.location.hash,
      },
      { replace: true, ...options }
    );
  }
);

// Navigation state atom (pending navigation info)
export const navigationAtom = atom((get) => {
  const state = get(routerStateReadyAtom);
  return state.navigation;
});

// History action atom
export const historyActionAtom = atom((get) => {
  const state = get(routerStateReadyAtom);
  return state.historyAction;
});

// Getter functions for non-React contexts
export const getLocation = (): Location => {
  return getDefaultStore().get(locationAtom);
};

export const getMatches = (): UIMatch[] => {
  return getDefaultStore().get(matchesAtom);
};

export const getSearchParams = (): URLSearchParams => {
  return getDefaultStore().get(searchParamsAtom);
};

// Navigate function for non-React contexts
export const navigate = (
  to: To | number,
  options?: NavigateOptions
): Promise<void> => {
  const store = getDefaultStore();
  if (typeof to === 'number') {
    return store.set(locationAtom, to);
  } else {
    return store.set(locationAtom, { to, options });
  }
};

// React hooks
export const useRouterLocation = () => useAtomValue(locationAtom);
export const useRouterNavigate = () => useSetAtom(locationAtom);
export const useRouterMatches = () => useAtomValue(matchesAtom);
export const useRouterSearchParams = () => useAtom(searchParamsAtom);
export const useRouterNavigation = () => useAtomValue(navigationAtom);

// Utility component for early fail if the app is not wrapped with `createJotaiRouter`
export const InitRouter = () => {
  useAtomValue(routerStateReadyAtom);
  return null;
};

// Factory function - initializes router state and subscribes to changes
export const createJotaiRouter = (router: ReactRouter): ReactRouter => {
  const store = getDefaultStore();

  // Store router instance for navigation
  routerInstance = router;

  // Set initial state
  store.set(routerStateAtom, router.state);

  // Subscribe to state changes and update the atom
  // No need to unsubscribe, because the subscription is tied to the lifetime of the entire app
  router.subscribe((state) => {
    store.set(routerStateAtom, state);
  });

  return router;
};
